import {
	flatten,
	filter,
	find,
	findIndex,
	some,
	forEach,
	groupBy
} from "lodash";
import {
	REPORT_SCOPES,
	EVENT_PROCESS_UI_RULES
} from "constants.js";
import {
	TRIGGER_TYPES,
	AGGREGATION_ACTIONS
} from "./constants.js";
import { hasSectionCountRule } from "../../services/details-count-condition";

export function controller(
	$q,
	triggerMethods,
	detailHelper,
	ruleService,
	$timeout,
	$logger
) {
	const ctrl = this;
	const observers = [];
	const providers = {};

	ctrl.errors = [];

	ctrl.notify = notify;
	ctrl.registerValueProvider = registerValueProvider;
	ctrl.subscribe = subscribe;
	ctrl.validate = validate;
	ctrl.process = process;
	ctrl.getValuesByType = getValuesByType;

	ctrl.$onInit = () => {
		ctrl.eventBus = ctrl.reportScope.events;
		ctrl.validationService = ctrl.reportScope.validationService;
	};

	function registerValueProvider(types, provider, scope) {
		const isArray = Array.isArray(types);

		if(!isArray) {
			types = [types];
		}

		forEach(types, type => {
			providers[type] = providers[type] || [];
			providers[type].push(provider);
		});

		if(!scope) {
			return;
		}

		scope.$on("$destroy", () => {
			forEach(types, type => {
				providers[type] = providers[type].filter(p => p != provider);
				if(!providers[type].length) {
					delete providers[type];
				}
			});
		});
	}

	function subscribe(callback) {
		observers.push(callback);
		return function unsubscribe() {
			const idx = findIndex(observers, x => x === callback);
			if(idx !== -1) {
				observers.splice(idx, 1);
			}
		};
	}

	function notify(scopes) {
		let validatePromise = $q.resolve(ctrl.errors);
		if(scopes) {
			validatePromise = validate(scopes);
		}

		return validatePromise.then(errors => {
			ctrl.errors = errors;
			observers.forEach(cb => cb(validatePromise));
			return errors;
		});
	}

	function getValuesByType(scopes) {
		const valuesArrays = {};

		scopes
			.map(s => {
				if(s === REPORT_SCOPES.metadata) {
					valuesArrays[s] = [];
				}
				if(providers[s]?.length) {
					const f = providers[s]
						.map(get => {
							const val = get();
							if(val === null) {
								return [];
							}
							return val.filter(v => v.IsDeleted !== true && v.hasResultForRuleRun !== false);
						});

					valuesArrays[s] = flatten(f);
				}
			});

		return valuesArrays;
	}

	function mergeAllScopedValues(values) {
		const allValues = detailHelper.mergeDetails([
			values[REPORT_SCOPES.report] ?? [],
			values[REPORT_SCOPES.reportAndDetails] ?? [],
			values[REPORT_SCOPES.details] ?? [],
			values[REPORT_SCOPES.detail] ?? []
		]);

		return allValues;
	}

	function validate(evalScopes, detailId, values) {
		const valueScopes = [...evalScopes];

		const validationRules = filter(ctrl.reportScope.form.Rules, r =>
			some(r.Triggers, t => t.Type.Code === TRIGGER_TYPES.validation));

		const filteredRules = filter(validationRules, (r) => {
			return some(evalScopes, (s) => r.validationScope !== undefined ? r.validationScope.Code === s : r.Scope.Code === s);
		});

		const groupedRules = groupBy(filteredRules, (rule) => rule.Scope.Code);

		if(valueScopes.indexOf(REPORT_SCOPES.details) > -1 && valueScopes.indexOf(REPORT_SCOPES.detail) === -1) {
			valueScopes.push(REPORT_SCOPES.detail);
		}

		const allValuesByScope = getValuesByType(valueScopes);
		// Parent header conditions need their values during a details-only
		// notification. Collect them separately to preserve legacy rule inputs.
		const countParentValues = filteredRules.some(hasSectionCountRule) && !valueScopes.includes(REPORT_SCOPES.report)
			? getValuesByType([REPORT_SCOPES.report]) : {};

		values = values ?? allValuesByScope;

		if(valueScopes.indexOf(REPORT_SCOPES.details) > -1 && !!values[REPORT_SCOPES.detail]) {
			values[REPORT_SCOPES.details] = detailHelper.mergeDetails([values[REPORT_SCOPES.details], values[REPORT_SCOPES.detail]]);
		}

		let ruleEvaluations = [];

		forEach(evalScopes, (scope) => {
			const countRules = (groupedRules[scope] ?? []).filter(hasSectionCountRule);
			if((allValuesByScope[scope] === undefined || values[scope] === undefined) && countRules.length === 0) {
				return true;
			}

			if(scope === REPORT_SCOPES.detail) {
				values[scope] = values[scope].map(d => {
					return {
						...d,
						DetailId: detailId
					};
				});

				groupedRules[scope] = !!groupedRules[scope] && groupedRules[scope].map(r => {
					return {
						...r,
						DetailId: detailId
					};
				}) || [];

				const zeroDetailIdValues = allValuesByScope[scope].filter(v => v.DetailId === 0);
				zeroDetailIdValues.forEach(v => v.DetailId = detailId);

				if(detailHelper.isDetailPivoted(values[scope])) {
					const detailValues = filter(values[scope], (val) => val.DetailId === detailId);

					const detailScopedRules = groupedRules[scope].map(r => {
						return {
							...r,
							DetailLinkKey: detailValues[0].DetailLinkKey,
							repeatId: detailValues[0].repeatId
						};
					});

					const scopeEvals = process(detailScopedRules, detailValues, detailValues[0].DetailId);
					ruleEvaluations = ruleEvaluations.concat(scopeEvals);
				}
				else {
					const allValues = mergeAllScopedValues(allValuesByScope);
					const scopeEvals = process(groupedRules[scope], allValues, detailId);
					ruleEvaluations = ruleEvaluations.concat(scopeEvals);
				}
			}
			else if(scope === REPORT_SCOPES.reportAndDetails) {
				groupedRules[scope] = !!groupedRules[scope] && groupedRules[scope].map(r => {
					return {
						...r,
						DetailId: r.isAggregate ? undefined : detailId
					};
				}) || [];

				const allValues = mergeAllScopedValues(allValuesByScope);
				const scopeEvals = process(groupedRules[scope], allValues, detailId);
				ruleEvaluations = ruleEvaluations.concat(scopeEvals);
			}
			else {
				const allValues = mergeAllScopedValues(allValuesByScope);
				const otherRules = (groupedRules[scope] ?? []).filter(rule => !hasSectionCountRule(rule));
				const scopeEvals = allValuesByScope[scope] === undefined ? [] : process(otherRules, allValues);
				ruleEvaluations = ruleEvaluations.concat(scopeEvals);
				if(countRules.length > 0) {
					const parentValues = mergeAllScopedValues({ ...countParentValues, ...allValuesByScope, ...values });
					ruleEvaluations.push(process(countRules, parentValues));
				}
			}
		});

		return $q
			.all([...ruleEvaluations])
			.then(validations => {
				const allScopeValidations = flatten(validations);
				const failed = allScopeValidations.filter(x => x.result === false);

				return failed;
			});
	}

	function executeTriggers(rule, headerResult, detailResult) {
		let visibilityChanged = false;
		rule.Triggers
			.filter(t => t.Type.Code !== "VALIDATION" && t.Operator.Code !== "FILTER")
			.forEach(t => {
				if(!!t.Section && !t.Field) {
					return;
				}

				const field = t.Field;
				const matched = !!field?.IsDetail ? detailResult : headerResult;

				let triggerMetadata = triggerMethods.methods[t.Operator.Code](field, t, matched, rule, ctrl.eventBus);
				if(field && ((field.IsDetail && detailResult === undefined) || (!field.IsDetail && detailResult !== undefined))) {
					triggerMetadata = undefined;
				}

				visibilityChanged = visibilityChanged || !!triggerMetadata?.visibilityChanged;
			});

		return { visibilityChanged };
	}

	async function process(rules = [], allValues, processingDetailId, scope) {
		const {
			detailValuesById,
			headerValue,
			detailValues
		} = ruleService.groupValuesByDetailId(allValues, processingDetailId);

		let headerAndThisDetailValues = [...(headerValue ?? [])];
		if(processingDetailId !== undefined) {
			const thisDetailValues = (allValues ?? []).filter(v => v.DetailId === processingDetailId);
			headerAndThisDetailValues = headerAndThisDetailValues.concat(thisDetailValues);
		}

		const allProcessedRules = rules.map(async rule => {
			if(!!rule.Aggregation) {
				const aggregationAction = AGGREGATION_ACTIONS[rule.Aggregation.Code];
				const detailResults = [];

				forEach(detailValues, (detail) => {
					const detailId = detail.find(d => d.DetailId !== undefined).DetailId;
					const comboVal = detailValuesById[detailId];

					const ruleResult = ruleService.processSingleRule(rule, comboVal, detailValuesById, processingDetailId, ctrl.reportScope)
						.then(result => ({ result, detailId }));
					detailResults.push(ruleResult);
				});

				const results = await Promise.all(detailResults);
				const aggregatedResult = aggregationAction(results.map(r => r.result));

				return aggregatedResult;
			}

			if(rule.Scope.Code === REPORT_SCOPES.reportAndDetails && !rule.hasAggregation) {
				const comboResults = [];

				if((rule.visibilityScope?.Code === REPORT_SCOPES.detail || rule.validationScope?.Code === REPORT_SCOPES.detail) && processingDetailId !== undefined) {
					const comboVal = detailValuesById[processingDetailId];

					const singleRuleResult = ruleService.processSingleRule(rule, comboVal, detailValuesById, processingDetailId, ctrl.reportScope)
						.then(result => ({ result, detailId: processingDetailId }));
					comboResults.push(singleRuleResult);
				}
				else {
					forEach(detailValues, (detail) => {
						const detailId = detail[0].DetailId;
						const comboVal = detailValuesById[detailId];

						const singleRuleResult = ruleService.processSingleRule(rule, comboVal, detailValuesById, processingDetailId, ctrl.reportScope)
							.then(result => ({ result, detailId }));
						comboResults.push(singleRuleResult);
					});
				}

				const r = await Promise.all(comboResults);

				const triggerType = rule.Triggers && rule.Triggers[0] && rule.Triggers[0].Type.Code;

				if(triggerType === TRIGGER_TYPES.validation) {
					const result = some(r, result => !result.result);
					rule.result = !result;
					rule.results = r;
				}
				else {
					const result = some(r, result => result.result);
					rule.result = result;
					rule.results = r;
				}

				return rule;
			}

			const result = await ruleService.processSingleRule(rule, headerAndThisDetailValues, detailValuesById, processingDetailId, ctrl.reportScope);

			rule.result = result;
			const detailId = processingDetailId;
			if((rule.Scope.Code === REPORT_SCOPES.detail || rule.validationScope.Code === REPORT_SCOPES.reportAndDetails) && detailId !== undefined && detailId !== null) {
				rule.results = [
					{ result: result, detailId: detailId }
				];
			}
			else {
				rule.results = [
					{ result: result }
				];
			}

			return rule;
		});

		let shouldReRun = false;
		return Promise.all(allProcessedRules)
			.then(validatedRules => {
				const mapping = validatedRules.map(r => ({
					Id: r.Id,
					Title: r.Title,
					Operator: r.Operator?.Code,
					Result: r.result,
					Scope: `(${r.validationScope?.Code},${r.visibilityScope?.Code})`,
					Details: r
				}));

				if(mapping.length > 0) {
					$logger.groupCollapsed(`Processed ${mapping.length} Rules ${new Date().toISOString()}`);
					$logger.table(mapping);
					$logger.groupEnd();
				}

				return validatedRules;
			})
			.then(validatedRules => {
				forEach(validatedRules, vr => {
					let thisResult = {};

					if(processingDetailId !== undefined) {
						thisResult = find(vr.results, r => r.detailId === processingDetailId) || {};
					}
					else if(scope === REPORT_SCOPES.detail) {
						thisResult = vr;
					}

					if(vr.Triggers && thisResult) {
						const triggerReturn = executeTriggers(vr, vr.result, thisResult.result);

						shouldReRun = shouldReRun || (!!triggerReturn && triggerReturn.visibilityChanged);
					}
				});
				return validatedRules;
			})
			.then(validatedRules => {
				if(shouldReRun) {
					$timeout(() => {
						ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, {
							force: true,
							detailId: processingDetailId,
							field: { IsDetail: (processingDetailId !== undefined || scope === REPORT_SCOPES.detail) }
						});
					});
				}

				return validatedRules;
			});
	}
}

export default angular
	.module("vesl.screens.rule-provider", [])
	.component("ruleProvider", {
		transclude: true,
		template: "<ng-transclude></ng-transclude>",
		controller,
		controllerAs: "$ctrl",
		require: {
			reportScope: "^^reportScope"
		}
	}).name;
